import { useState } from 'react';
import { toast } from 'react-toastify';
import { requestAgentBridgeUrl } from '../services/api';

/**
 * Crossing into the builder workspace, the one way across, shared by every
 * surface that offers it (the top-right switcher, the onboarding card, and a
 * project row continuing in the Planner).
 *
 * `destination` is a path inside the builder. Without one the crossing lands on
 * the workspace's own home; with one it lands on that exact screen, which is
 * what makes "carry on where you left off" different from "go and find it".
 *
 * It may also be a function returning that path, for the callers that have to
 * ask a server where to land before they know. That is the only reason this
 * takes a thunk: the tab has to exist BEFORE any await (see below), so a caller
 * cannot resolve the path first and pass a plain string.
 *
 * Lifted out of ContextSwitcher when a second caller appeared: the sequence
 * below is subtle enough that a copy would drift. The tab MUST be opened
 * synchronously inside the click, before the await — a popup opened after an
 * await is blocked by the browser, which is the bug this ordering exists to
 * avoid.
 */
type Destination = string | null | undefined | (() => Promise<string>);

/* The server's own sentence when it has one. These endpoints refuse with a
   reason a person can act on ("only the person who asked ... can carry on
   scoping it", "this company does not have a Workspace yet"), and collapsing
   every one of them into a single generic line left the reader with nothing to
   do next and no way to tell a permission problem from an outage. */
/* The new tab is blank until the crossing answers, which can take a while the
   first time. A blank white tab reads as broken, so it says what is happening.
   An already open Workspace tab is another origin and cannot be written to;
   it is brought forward instead. */
const OPENING_PAGE = `<!doctype html><html><head><meta charset="utf-8"><title>Opening your workspace</title>
<style>body{margin:0;height:100vh;display:grid;place-items:center;background:#f7f7f5;color:#18191d;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.box{text-align:center}.dot{width:36px;height:36px;margin:0 auto 14px;border-radius:50%;background:#18191d;display:grid;place-items:center;animation:p 1.4s ease-in-out infinite}
.dot i{width:12px;height:12px;border-radius:50%;background:#f5d90a}p{margin:0;color:#5f6168}@keyframes p{50%{transform:scale(.88)}}
@media (prefers-color-scheme:dark){body{background:#141518;color:#f2f2f0}.dot{background:#f2f2f0}p{color:#a3a5ab}}</style></head>
<body><div class="box"><div class="dot"><i></i></div><b>Opening your workspace</b><p>This can take up to a minute the first time.</p></div></body></html>`;

function showOpening(tab: Window | null) {
  if (!tab) return;
  try {
    tab.document.open();
    tab.document.write(OPENING_PAGE);
    tab.document.close();
  } catch {
    // An existing Workspace tab on the builder's origin.
  }
  tab.focus();
}

const reasonFrom = (err: unknown): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return 'Could not open the workspace. Try again in a moment.';
};

export function useOpenWorkspace() {
  const [opening, setOpening] = useState(false);

  const open = async (workspaceId?: string | null, destination?: Destination) => {
    if (!workspaceId || opening) return;
    setOpening(true);
    // Always begin with a fresh, same-origin handoff tab. Reusing a named
    // Builder tab is unsafe after Builder's cross-origin isolation severs its
    // opener relationship: Chrome can still return a WindowProxy for that
    // name, but assigning the signed crossing URL throws and the click appears
    // to do nothing.
    const tab = window.open('', '_blank');
    showOpening(tab);
    try {
      let path: string | undefined;
      if (typeof destination === 'function') path = await destination();
      else if (destination) path = destination;
      const { url } = await requestAgentBridgeUrl(path, workspaceId);
      if (!url) throw new Error('No crossing URL returned');
      if (tab && !tab.closed) {
        try {
          tab.location.replace(url);
          tab.focus();
        } catch {
          // The browser invalidated the pre-opened context. The signed URL is
          // already ready, so finish the crossing in this tab rather than
          // losing the user's click.
          window.location.assign(url);
        }
      } else {
        window.location.assign(url); // popup blocked → fall back in place
      }
    } catch (err) {
      if (tab) tab.close();
      toast.error(reasonFrom(err));
    } finally {
      setOpening(false);
    }
  };

  return { opening, open };
}

export default useOpenWorkspace;
